import type { CityConfig } from "@/pages/city-template/types";

export const happyValleyConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Clackamas County Clerk Vital Records", desc: "Obtain vital records through the Clackamas County Clerk's office.", url: "https://www.clackamas.us/recording" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Happy Valley",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "happy-valley",
  route: "/cities/happy-valley",
  title: "Emergency Passport Happy Valley, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Happy Valley, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Clackamas County and southeast Portland metro travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Happy Valley, Oregon",
  heroSubtitle: "Passport expired before your family trip to Mexico? Flying out of PDX in days? Fast Passport Center helps Happy Valley travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Happy%20Valley%20Oregon%20suburban%20residential%20neighborhood%20with%20green%20hills%20and%20Mount%20Hood%20visible%20in%20the%20distance%2C%20affluent%20family%20homes%20with%20manicured%20lawns%2C%20Pacific%20Northwest%20landscape%20with%20evergreen%20trees%2C%20soft%20golden%20hour%20light%2C%20photorealistic%20landscape%20photography%20showing%20southeast%20Portland%20metro%20area%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=happy-valley-hero-01&orientation=landscape",
  heroImageAlt: "Happy Valley Oregon suburban landscape with green hills and Mount Hood in the distance",
  geoPosition: "45.4462;-122.5300",
  geoPlacename: "Happy Valley, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Happy Valley", zips: "97086" },
    { name: "Clackamas", zips: "97015" },
    { name: "Oregon City", zips: "97045" },
    { name: "Milwaukie", zips: "97222, 97267" },
    { name: "Gladstone", zips: "97027" },
    { name: "West Linn", zips: "97068" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "Damascus", zips: "97089" },
    { name: "Gresham", zips: "97030, 97080" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Tualatin", zips: "97062" },
    { name: "Canby", zips: "97013" },
    { name: "Molalla", zips: "97038" },
    { name: "Estacada", zips: "97023" },
    { name: "Sandy", zips: "97055" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Happy Valley home in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Happy Valley", desc: "FedEx and UPS locations throughout Clackamas County offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute family travel." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Jennifer L.",
      location: "Happy Valley, OR",
      rating: 5,
      text: "Family vacation to Cancun in 12 days and my husband's passport expired. Fast Passport Center renewed it in 5 days. We shipped from the Clackamas FedEx. Absolutely stress-free experience.",
      service: "Passport Renewal",
    },
    {
      name: "Marcus W.",
      location: "Happy Valley, OR",
      rating: 5,
      text: "Business trip to Tokyo announced with 10 days notice. Fast Passport Center handled everything and my passport arrived in 4 days. Their daily email updates kept me calm the whole time.",
      service: "Emergency Passport",
    },
    {
      name: "Sarah K.",
      location: "Clackamas, OR",
      rating: 5,
      text: "Our twins needed passports for a school trip to England. I had no idea where to start. Fast Passport Center sent a perfect checklist and we had both passports in 9 days. Clackamas mom forever grateful.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "happy-valley-post",
      name: "Happy Valley Post Office",
      type: "USPS Passport Acceptance",
      address: "10827 SE Main St",
      city: "Happy Valley",
      state: "OR",
      zip: "97086",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Happy Valley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Happy Valley. Appointment required — book online in advance. Photo services may be available.",
      icon: "ri-mail-line",
    },
    {
      id: "clackamas-post",
      name: "Clackamas Post Office",
      type: "USPS Passport Acceptance",
      address: "15715 SE 82nd Dr",
      city: "Clackamas",
      state: "OR",
      zip: "97015",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm, Sat 9am–12pm",
      distance: "Clackamas — about 3 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Clackamas Town Center area option for north Happy Valley residents.",
      icon: "ri-mail-line",
    },
    {
      id: "oregon-city-post",
      name: "Oregon City Post Office",
      type: "USPS Passport Acceptance",
      address: "515 High St",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Oregon City — about 5 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Historic Oregon City option for west Happy Valley residents.",
      icon: "ri-mail-line",
    },
    {
      id: "milwaukie-post",
      name: "Milwaukie Post Office",
      type: "USPS Passport Acceptance",
      address: "11001 SE Main St",
      city: "Milwaukie",
      state: "OR",
      zip: "97222",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Milwaukie — about 8 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Milwaukie option for north Happy Valley and Sellwood-Moreland area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "portland-pearl",
      name: "Portland Main Post Office",
      type: "USPS Passport Acceptance",
      address: "715 NW Hoyt St",
      city: "Portland",
      state: "OR",
      zip: "97208",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Portland Pearl District — about 12 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Portland's main passport acceptance facility. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "gresham-post",
      name: "Gresham Main Post Office",
      type: "USPS Passport Acceptance",
      address: "224 W Powell Blvd",
      city: "Gresham",
      state: "OR",
      zip: "97030",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm, Sat 9am–12pm",
      distance: "Gresham — about 10 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Gresham option for northeast Happy Valley residents.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Happy Valley before international travel?",
      a: "Yes. Through Fast Passport Center, Happy Valley travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — ideal for Clackamas County and southeast Portland metro travel plans.",
    },
    {
      q: "How fast can I renew my passport in Happy Valley?",
      a: "Fast passport renewal in Happy Valley is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my family trip to Mexico?",
      a: "If your passport expires before a family trip to Mexico, don't wait. Mexico requires your passport to be valid for the duration of your stay. Fast Passport Center can expedite your renewal so you don't miss your vacation. The sooner you start, the more options you have.",
    },
    {
      q: "Can I get a same-day passport appointment near Happy Valley?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Happy Valley, Fast Passport Center is the faster and more predictable option — no Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in Happy Valley?",
      a: "Yes. Child passport expediting in Happy Valley is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps.",
    },
    {
      q: "Does Happy Valley have a passport agency?",
      a: "Happy Valley, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north) and the San Francisco Passport Agency in California (about 640 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Happy Valley residents.",
    },
    {
      q: "How long does a standard passport take in Happy Valley?",
      a: "If you apply through a standard post office acceptance facility in Happy Valley, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Happy Valley travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in Happy Valley without visiting an office?",
      a: "Yes. Most Happy Valley-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in Clackamas County.",
    },
    {
      q: "What airports do Happy Valley travelers typically fly from?",
      a: "Happy Valley travelers typically use Portland International Airport (PDX) — about 14 miles northwest — for all domestic and international departures. PDX offers direct flights to Mexico, Canada, Europe, and Asia. If your passport isn't in hand, don't book international travel. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Happy Valley", address: "10827 SE Main St, Happy Valley, OR 97086", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/store-locator" },
    { name: "CVS — Clackamas", address: "15815 SE 82nd Ave, Clackamas, OR 97015", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/store-locator/cvs-pharmacy-locations" },
    { name: "FedEx Office — Clackamas", address: "12130 SE 82nd Ave, Clackamas, OR 97015", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM", website: "https://local.fedex.com/" },
    { name: "The UPS Store — Clackamas", address: "10117 SE Sunnyside Rd, Clackamas, OR 97015", hours: "Mon–Fri 8 AM – 7 PM", website: "https://www.theupsstore.com/tools/find-a-store" },
    { name: "Walmart — Oregon City", address: "16301 SE 82nd Dr, Clackamas, OR 97015", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/store/finder" },
    { name: "Walgreens — Oregon City", address: "1900 Beavercreek Rd, Oregon City, OR 97045", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/store-locator" },
    { name: "CVS — Oregon City", address: "2000 Beavercreek Rd, Oregon City, OR 97045", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/store-locator/cvs-pharmacy-locations" },
    { name: "Walgreens — Milwaukie", address: "11001 SE Main St, Milwaukie, OR 97222", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/store-locator" },
    { name: "CVS — Milwaukie", address: "11211 SE Main St, Milwaukie, OR 97222", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/store-locator/cvs-pharmacy-locations" },
    { name: "Walmart — Gresham", address: "39000 Proctor Blvd, Sandy, OR 97055", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/store/finder" },
  ],
  dmvLocations: [
    {
      name: "Oregon City DMV Office",
      address: "1651 Beavercreek Rd, Oregon City, OR 97045",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Clackamas County DMV office near Happy Valley. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Clackamas DMV Office",
      address: "10117 SE Sunnyside Rd, Clackamas, OR 97015",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Clackamas option for north Happy Valley residents.",
      featured: false,
    },
    {
      name: "Portland DMV Office — McLoughlin",
      address: "9301 SE McLoughlin Blvd, Portland, OR 97222",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Portland option for north Happy Valley metro residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 14 miles northwest",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, Mexico City, and more. The busiest travel seasons are May–August and December–January.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 175 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Happy Valley travelers use SEA for additional flight options. Passport required for all international departures.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
    {
      code: "TTD",
      name: "Portland-Troutdale Airport",
      distance: "General aviation — about 12 miles north",
      address: "2023 E 223rd Ave, Gresham, OR 97030",
      note: "Portland-Troutdale Airport is general aviation only — no commercial flights. International travelers use PDX.",
      flightUrl: "https://www.portofportland.com/ttd",
      flightLabel: "TTD Airport Info",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PDX in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Happy Valley and Clackamas County.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Happy Valley and Clackamas County travelers renew fast — perfect for family vacations and business trips.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Clackamas County", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Happy Valley travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Happy Valley families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Happy Valley residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for Happy Valley travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Happy Valley, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Happy Valley residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Happy Valley, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Happy Valley travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Happy Valley families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Happy Valley, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Happy Valley families often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip, especially for summer and holiday travel from PDX." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in Happy Valley." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Happy Valley runs 6–13 weeks. If your trip to Mexico, Canada, or Europe is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Happy Valley travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Happy Valley", description: "Drop off at any FedEx or UPS in the Clackamas County area — most locations are open evenings. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from Happy Valley" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Oregon City", route: "/cities/oregon-city" },
    { name: "Emergency passport help near Clackamas", route: "/states/oregon" },
    { name: "Rush passport service in Milwaukie", route: "/cities/milwaukie" },
    { name: "Fast passport renewal for Clackamas County travelers", route: "/states/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Clackamas County — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Happy Valley and all of Clackamas County remotely",
  serviceAreaIntro: "Whether you are in downtown Happy Valley, Clackamas, Damascus, or anywhere in Clackamas County and the southeast Portland metro, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Happy Valley-area zip codes. Anywhere in Clackamas, Multnomah, or eastern Washington County — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Happy Valley as simple as possible. Most Happy Valley clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Happy Valley travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from Happy Valley, Clackamas, and southeast Portland metro travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Happy Valley and Clackamas County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};